<template>
  <div class="modal fade dq-payroll-modal" id="add_payroll" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-md">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header">
          <div class="dq-modal-titlewrap">
            <div class="dq-hero-icon dq-hero-blue">
              <i class="ti ti-cash"></i>
            </div>
            <div>
              <h4 class="modal-title dq-modal-title mb-0">
                {{ isEditMode ? "Edit Payment" : "Add Payment" }}
              </h4>
              <div class="dq-modal-sub">
                Configure payment details and assign employees if needed.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetForm"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="onSubmit">
          <div class="modal-body dq-modal-body pb-0">
            <!-- Section: Basics -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-blue">
                    <i class="ti ti-info-circle"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Basic Info</div>
                    <div class="dq-help">Give the addition a clear name and category.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3">
                  <div class="col-md-12">
                    <label class="form-label dq-label">Name</label>
                    <input
                      v-model="form.name"
                      type="text"
                      class="form-control dq-input"
                      placeholder="e.g. Leave Balance Amount"
                      required
                    />
                  </div>

                  <div class="col-md-12">
                    <label class="form-label dq-label">Category Name</label>
                    <vue-select
                      v-model="form.category"
                      :options="['Monthly Remuneration', 'Additional Remuneration']"
                      placeholder="Select category"
                      class="dq-vselect"
                    />
                    <div class="dq-help mt-1">
                      Monthly is recurring; Additional is occasional/one-off.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section: Amount -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-purple">
                    <i class="ti ti-calculator"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Amount</div>
                    <div class="dq-help">Set a default value or enable unit calculation.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="row g-3 align-items-end">
                  <div class="col-md-8">
                    <label class="form-label dq-label">Default/Unit Amount</label>
                    <input
                      v-model.number="form.amount"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control dq-input"
                      placeholder="Enter amount e.g. 5000"
                      required
                    />
                  </div>

                  <div class="col-md-4">
                    <div class="dq-switch-card">
                      <div class="dq-switch-left">
                        <div class="dq-switch-title">Unit</div>
                        <div class="dq-help mb-0">Multiply by units</div>
                      </div>
                      <div class="form-check form-switch m-0">
                        <input
                          class="form-check-input dq-switch"
                          type="checkbox"
                          v-model="form.unit_calculation"
                          role="switch"
                          id="unitCalculationSwitch"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section: Assignee -->
            <div class="dq-section">
              <div class="dq-section-head">
                <div class="dq-section-title">
                  <span class="dq-section-badge dq-badge-green">
                    <i class="ti ti-users"></i>
                  </span>
                  <div>
                    <div class="dq-h6">Assignee</div>
                    <div class="dq-help">Choose who this addition applies to.</div>
                  </div>
                </div>
              </div>

              <div class="dq-section-body">
                <div class="dq-radio-grid">
                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      v-model="form.assignee_type"
                      value="none"
                      id="assigneeNone"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-minus"></i>
                      <span>
                        <span class="dq-radio-title">No assignee</span>
                        <span class="dq-radio-sub">Applies to none by default</span>
                      </span>
                    </span>
                  </label>

                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      v-model="form.assignee_type"
                      value="all"
                      id="assigneeAll"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-stack-2"></i>
                      <span>
                        <span class="dq-radio-title">All employees</span>
                        <span class="dq-radio-sub">Applies to everyone</span>
                      </span>
                    </span>
                  </label>

                  <label class="dq-radio">
                    <input
                      class="form-check-input"
                      type="radio"
                      v-model="form.assignee_type"
                      value="select"
                      id="assigneeSelect"
                    />
                    <span class="dq-radio-ui">
                      <i class="ti ti-user-plus"></i>
                      <span>
                        <span class="dq-radio-title">Select employees</span>
                        <span class="dq-radio-sub">Pick specific staff</span>
                      </span>
                    </span>
                  </label>
                </div>

                <div v-if="form.assignee_type === 'select'" class="mt-3">
                  <label class="form-label dq-label">Select Employee(s)</label>

                  <div
                    class="dropdown w-100 dq-dropdown-wrap"
                    data-bs-auto-close="outside"
                    data-bs-display="static"
                  >
                    <button
                      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-dropdown-btn"
                      type="button"
                      data-bs-toggle="dropdown"
                    >
                      <span class="text-truncate d-flex align-items-center gap-2">
                        <i class="ti ti-users"></i>
                        <span class="text-truncate">{{ selectedEmployeesLabel }}</span>
                      </span>
                      <i class="ti ti-chevron-down ms-2"></i>
                    </button>

                    <div
                      class="dropdown-menu p-2 w-100 shadow dq-dropdown"
                      @click.stop
                    >
                      <div class="dq-search">
                        <i class="ti ti-search"></i>
                        <input
                          v-model="employeeQuery"
                          type="text"
                          class="form-control dq-search-input"
                          placeholder="Search employees..."
                          autocomplete="off"
                        />
                      </div>

                      <div class="dq-list" style="max-height: 240px; overflow-y: auto">
                        <button
                          v-for="emp in filteredEmployees"
                          :key="emp.id"
                          type="button"
                          class="dropdown-item dq-item d-flex justify-content-between align-items-center"
                          @click="toggleEmployee(emp.id)"
                        >
                          <span class="text-truncate">
                            {{ emp.first_name }} {{ emp.last_name || "" }}
                            <span class="dq-muted">({{ emp.employee_id }})</span>
                          </span>

                          <span
                            v-if="isEmployeeSelected(emp.id)"
                            class="dq-chip"
                          >
                            <i class="ti ti-check me-1"></i> Selected
                          </span>
                        </button>

                        <div
                          v-if="filteredEmployees.length === 0"
                          class="text-center py-2 small text-muted"
                        >
                          No employee found
                        </div>
                      </div>

                      <div class="dq-drop-footer" v-if="form.employee_ids.length">
                        <span class="dq-help mb-0">
                          {{ form.employee_ids.length }} selected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="error" class="alert alert-danger my-2 dq-alert">{{ error }}</div>
            <div v-if="success" class="alert alert-success my-2 dq-alert">{{ success }}</div>
          </div>

          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border me-2 dq-btn-muted"
              data-bs-dismiss="modal"
              @click="resetForm"
            >
              Cancel
            </button>

            <button type="submit" class="btn btn-primary dq-btn-primary">
              <span
                v-if="payrollAdditionStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              {{ isEditMode ? "Save Changes" : "Add Payment" }}
              <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- DELETE MODAL -->
  <div class="modal fade dq-confirm-modal" id="deletepayrollModal" tabindex="-1" ref="deleteModalEl">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-body text-center dq-confirm-body">
          <div class="dq-confirm-icon">
            <i class="ti ti-trash-x"></i>
          </div>
          <h4 class="mb-1 dq-confirm-title">Confirm Delete</h4>
          <p class="mb-3 dq-confirm-sub">
            This record will be permanently removed. This action cannot be undone.
          </p>

          <div class="d-flex justify-content-center gap-2">
            <button class="btn btn-light dq-btn-muted" data-bs-dismiss="modal">
              Cancel
            </button>
            <button class="btn btn-danger dq-btn-danger" @click="confirmDelete">
              <span
                v-if="payrollAdditionStore.isLoading"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, defineExpose } from "vue";

import { usePayrollAdditionStore } from "@/stores/payrollAdditionStore";
import { useEmployeeStore } from "@/stores/employeeStore";
import Modal from "bootstrap/js/dist/modal";

const payrollAdditionStore = usePayrollAdditionStore();
const employeeStore = useEmployeeStore();

const emit = defineEmits(["refreshAdditions"]);

onMounted(() => {
  employeeStore.fetchEmployees({ status: "Active", per_page: 5000 });
});

// search + filter
const employeeQuery = ref("");

const activeEmployees = computed(() =>
  employeeStore.employees.filter(
    (emp) => String(emp?.status || "").trim().toLowerCase() === "active",
  ),
);

const filteredEmployees = computed(() => {
  const q = employeeQuery.value.trim().toLowerCase();
  if (!q) return activeEmployees.value;
  return activeEmployees.value.filter((emp) => {
    const fullName = `${emp.first_name} ${emp.last_name || ""}`.toLowerCase();
    return (
      fullName.includes(q) || (emp.employee_id || "").toLowerCase().includes(q)
    );
  });
});

// label for button
const selectedEmployeesLabel = computed(() => {
  if (!form.employee_ids.length) return "Select employees";

  const selected = activeEmployees.value.filter((e) =>
    isEmployeeSelected(e.id),
  );
  if (!selected.length) return "Select employees";

  if (selected.length === 1) {
    const e = selected[0];
    return `${e.first_name} ${e.last_name || ""} (${e.employee_id})`;
  }

  return `${selected.length} employees selected`;
});

const isEditMode = ref(false);

const form = reactive({
  id: null,
  name: "",
  category: "",
  amount: 0,
  unit_calculation: false,
  assignee_type: "none",
  employee_ids: [], // array of employee IDs
});

const error = ref("");
const success = ref("");

// toggle selection
function isEmployeeSelected(id) {
  const normalized = Number(id);
  return form.employee_ids.some((x) => Number(x) === normalized);
}

function toggleEmployee(id) {
  const normalized = Number(id);
  const idx = form.employee_ids.findIndex((x) => Number(x) === normalized);
  if (idx === -1) {
    form.employee_ids.push(normalized);
  } else {
    form.employee_ids.splice(idx, 1);
  }
}

function resetForm() {
  form.id = null;
  form.name = "";
  form.category = "";
  form.amount = 0;
  form.unit_calculation = false;
  form.assignee_type = "none";
  form.employee_ids = [];
  employeeQuery.value = "";
  isEditMode.value = false;
  error.value = "";
  success.value = "";
}

function closeModal(id) {
  const modalEl = document.getElementById(id);
  if (modalEl) {
    modalEl.classList.remove("show");
    modalEl.style.display = "none";
    modalEl.setAttribute("aria-hidden", "true");
    modalEl.removeAttribute("aria-modal");
  }
  document.body.classList.remove("modal-open");
  document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
}

async function onSubmit() {
  error.value = "";
  success.value = "";

  if (!form.name || !form.category || !form.amount) {
    error.value = "Please fill all required fields.";
    return;
  }

  if (form.assignee_type === "select" && form.employee_ids.length === 0) {
    error.value = "Please select at least one employee.";
    return;
  }

  try {
    const payload = {
      name: form.name,
      category: form.category,
      amount: form.amount,
      unit_calculation: form.unit_calculation,
      assignee_type: form.assignee_type,
      employee_ids: form.assignee_type === "select" ? form.employee_ids : [],
    };

    if (!isEditMode.value) {
      await payrollAdditionStore.createAddition(payload);
    } else {
      await payrollAdditionStore.updateAddition(form.id, payload);
    }
    //payrollAdditionStore.fetchAll();

    setTimeout(() => {
      resetForm();
      closeModal("add_payroll");
      emit("refreshAdditions");
    }, 700);
  } catch (e) {
    error.value = e?.response?.data?.message || "Failed to save addition";
  }
}

function openForEdit(addition) {
  form.id = addition.id;
  form.name = addition.name;
  form.category = addition.category;
  form.amount = addition.default_amount ?? addition.amount ?? 0;
  form.unit_calculation = !!addition.unit_calculation;
  form.assignee_type = addition.assignee_type || "none";
  form.employee_ids = addition.employee_ids || [];
  employeeQuery.value = "";
  isEditMode.value = true;
  error.value = "";
  success.value = "";
}

function openForDelete(addition) {
  form.id = addition.id;
  form.name = addition.name;
  const deleteModalEl = ref(null);
  let modalInstance = null;

  deleteModalEl.value = document.getElementById("deletepayrollModal");

  if (!modalInstance) {
    modalInstance = new Modal(deleteModalEl.value, { backdrop: "static" });
  }
  modalInstance.show();
}

async function confirmDelete() {
  try {
    payrollAdditionStore.isLoading = true;
    await payrollAdditionStore.deleteAddition(form.id);

    // Show success message (you can implement a toast notification here)
    // e.g., toast("Addition deleted", "success");

    closeModal("deletepayrollModal");
    emit("refreshAdditions");
  } catch (e) {
    // Show error message (you can implement a toast notification here)
    // e.g., toast(e?.response?.data?.message || "Delete failed", "error");
  } finally {
    payrollAdditionStore.isLoading = false;
  }
}

defineExpose({
  openForEdit,
  resetForm,
  openForDelete,
});
</script>


<style scoped>
/* ===== Premium modal base (safe scoped) ===== */
.dq-modal-content{
  border: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 26px 70px rgba(16,24,40,.18);
}
.dq-modal-header{
  padding: 16px 16px;
  background:
    radial-gradient(900px 280px at 10% 0%, rgba(59,130,246,.14), transparent 60%),
    radial-gradient(700px 240px at 90% 10%, rgba(99,102,241,.12), transparent 55%),
    linear-gradient(180deg, #f8fafc, #ffffff);
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.dq-modal-titlewrap{ display:flex; align-items:center; gap:12px; }
.dq-hero-icon{
  width: 46px; height: 46px; border-radius: 16px;
  display:inline-flex; align-items:center; justify-content:center;
  border: 1px solid rgba(0,0,0,.06);
  font-size: 20px;
}
.dq-hero-blue{ background: rgba(59,130,246,.12); color: rgba(37,99,235,1); }
.dq-modal-title{ font-weight: 950; letter-spacing: -.02em; }
.dq-modal-sub{ font-size:.84rem; color: rgba(17,24,39,.62); margin-top: 2px; }
.dq-close{ opacity: .95; }

.dq-modal-body{ padding: 14px 16px; }
.dq-modal-footer{
  padding: 12px 16px;
  background: rgba(248,250,252,1);
  border-top: 1px solid rgba(0,0,0,.06);
}

.dq-section{
  border: 1px solid rgba(0,0,0,.06);
  border-radius: 18px;
  overflow: visible;
  background: #fff;
  margin-bottom: 12px;
}
.dq-section-head{
  padding: 12px 14px;
  background: rgba(248,250,252,1);
  border-bottom: 1px solid rgba(0,0,0,.06);
}
.dq-section-title{ display:flex; align-items:center; gap:12px; }
.dq-section-badge{
  width: 38px; height: 38px; border-radius: 14px;
  display:inline-flex; align-items:center; justify-content:center;
  border: 1px solid rgba(0,0,0,.04);
}
.dq-badge-blue{ background: rgba(59,130,246,.12); color: rgba(37,99,235,1); }
.dq-badge-purple{ background: rgba(99,102,241,.12); color: rgba(79,70,229,1); }
.dq-badge-green{ background: rgba(16,185,129,.12); color: rgba(5,150,105,1); }

.dq-section-body{ padding: 14px; }
.dq-h6{ font-weight: 950; color: rgba(17,24,39,.88); }
.dq-help{ font-size: .82rem; color: rgba(17,24,39,.6); }
.dq-label{ font-weight: 850; color: rgba(17,24,39,.82); }

.dq-input{
  border-radius: 14px !important;
  min-height: 44px;
  border: 1px solid rgba(0,0,0,.08) !important;
}
.dq-input:focus{
  border-color: rgba(59,130,246,.45) !important;
  box-shadow: 0 0 0 4px rgba(59,130,246,.12) !important;
}

/* Switch card */
.dq-switch-card{
  display:flex; align-items:center; justify-content:space-between; gap:10px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(99,102,241,.06);
  border: 1px solid rgba(99,102,241,.10);
}
.dq-switch-title{ font-weight: 950; color: rgba(17,24,39,.86); }
.dq-switch{ cursor:pointer; }

/* Assignee radio cards */
.dq-radio-grid{
  display:grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.dq-radio{
  border: 1px solid rgba(0,0,0,.06);
  border-radius: 16px;
  padding: 10px 12px;
  background: #fff;
  display:flex;
  align-items:center;
  gap: 10px;
  cursor: pointer;
  transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
}
.dq-radio:hover{
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(16,24,40,.08);
  border-color: rgba(59,130,246,.22);
}
.dq-radio input{ margin-top: 0.25rem; }
.dq-radio-ui{ display:flex; align-items:center; gap: 10px; width: 100%; }
.dq-radio-ui i{ font-size: 18px; color: rgba(17,24,39,.55); }
.dq-radio-title{ display:block; font-weight: 950; color: rgba(17,24,39,.88); }
.dq-radio-sub{ display:block; font-size: .82rem; color: rgba(17,24,39,.60); }

/* Dropdown */
.dq-dropdown-wrap {
  position: relative;
}
.dq-dropdown-btn{
  border-radius: 14px;
  min-height: 44px;
}
.dq-dropdown{
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,.06);
  box-shadow: 0 22px 48px rgba(16,24,40,.14);
  z-index: 1060;
}
.dq-search{ position: relative; margin-bottom: 8px; }
.dq-search i{
  position: absolute; left: 10px; top: 50%;
  transform: translateY(-50%);
  color: rgba(17,24,39,.45);
}
.dq-search-input{
  padding-left: 34px;
  border-radius: 12px;
  min-height: 40px;
}
.dq-item{
  border-radius: 12px;
  padding: 10px 10px;
}
.dq-item:hover{ background: rgba(59,130,246,.08); }
.dq-muted{ color: rgba(17,24,39,.55); }
.dq-chip{
  display:inline-flex; align-items:center;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(59,130,246,.12);
  border: 1px solid rgba(59,130,246,.16);
  color: rgba(37,99,235,1);
  font-weight: 850;
  font-size: .78rem;
  white-space: nowrap;
}
.dq-drop-footer{
  padding: 8px 10px;
  border-top: 1px solid rgba(0,0,0,.06);
  background: rgba(248,250,252,1);
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
}
.dq-alert{ border-radius: 14px; }

/* Buttons */
.dq-btn-primary{
  border-radius: 14px;
  padding: 10px 14px;
  box-shadow: 0 14px 26px rgba(37,99,235,.18);
}
.dq-btn-muted{ border-radius: 14px; padding: 10px 14px; }

/* Confirm modal */
.dq-confirm-body{ padding: 18px 16px; }
.dq-confirm-icon{
  width: 64px; height: 64px;
  border-radius: 22px;
  display:inline-flex; align-items:center; justify-content:center;
  background: rgba(239,68,68,.12);
  color: rgba(220,38,38,1);
  border: 1px solid rgba(239,68,68,.18);
  margin: 0 auto 10px auto;
  font-size: 26px;
}
.dq-confirm-title{ font-weight: 950; }
.dq-confirm-sub{ color: rgba(17,24,39,.6); }
.dq-btn-danger{ border-radius: 14px; padding: 10px 14px; }

@media (max-width: 576px){
  .dq-modal-body{ padding: 12px; }
  .dq-section-body{ padding: 12px; }
}
</style>
